// DataAnalyzer.js  (pure‑JS version)
import React, { useState } from "react";

/* -------------------------------------------------
   CONFIG
--------------------------------------------------*/
const SVG_W    = 500;   // px
const SVG_H    = 180;   // px
const BIG_BINS = 10;    // maximum number of histogram bars

/* -------------------------------------------------
   COMPONENT
--------------------------------------------------*/
export default function DataAnalyzer() {
  /* ---------- state ---------- */
  const [data, setData] = useState([]);

  /* ---------- helpers ---------- */
  const genRandom = (n = 10) =>
    setData(Array.from({ length: n }, () => Math.floor(Math.random() * 101)));

  /* ---------- derived stats ---------- */
  const sorted = [...data].sort((a, b) => a - b);

  const mean =
    sorted.length > 0
      ? sorted.reduce((s, v) => s + v, 0) / sorted.length
      : null;

  let median = null;
  if (sorted.length) {
    const mid = Math.floor(sorted.length / 2);
    median =
      sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  let modes = null;
  if (sorted.length) {
    const freq = {};
    sorted.forEach(v => (freq[v] = (freq[v] || 0) + 1));
    const maxFreq = Math.max(...Object.values(freq));
    if (maxFreq > 1)
      modes = Object.keys(freq)
        .filter(k => freq[k] === maxFreq)
        .join(", ");
  }

  const range =
    sorted.length ? sorted[sorted.length - 1] - sorted[0] : null;

  /* ---------- histogram bins ---------- */
  // if we have ≤ BIG_BINS points, give each its own bar; else cap at BIG_BINS
  const binCt = sorted.length <= BIG_BINS ? sorted.length : BIG_BINS;
  const BIN_W = SVG_W / binCt;
  const STEP  = 100 / binCt; // data‑unit width of each bin
  const bins  = Array(binCt).fill(0);

  sorted.forEach(v => {
    const idx = Math.min(binCt - 1, Math.floor(v / STEP));
    bins[idx] += 1;
  });
  const maxBin = Math.max(...bins, 1);

  /* ---------- bell‑curve sample (optional) ---------- */
  const bellPts = [];
  if (sorted.length >= 8 && mean !== null) {
    const variance =
      sorted.reduce((acc, v) => acc + (v - mean) ** 2, 0) / sorted.length;
    const sd = Math.sqrt(variance);

    const pdfPeak = 1 / (sd * Math.sqrt(2 * Math.PI));
    const scaleY  = (0.8 * SVG_H) / pdfPeak;

    for (let x = 0; x <= 100; x += 2) {
      const pdf =
        (1 / (sd * Math.sqrt(2 * Math.PI))) *
        Math.exp(-((x - mean) ** 2) / (2 * sd ** 2));
      bellPts.push([
        (x / 100) * SVG_W,
        SVG_H - pdf * scaleY
      ]);
    }
  }

  /* ---------- scales ---------- */
  const scaleX = v => (v / 100) * SVG_W;

  /* ---------- render ---------- */
  return (
    <div style={{ border: "1px solid #ccc", padding: 14, marginTop: 12 }}>
      <h4>Data Analyzer</h4>
      <button className="button1" onClick={() => genRandom(10)}>
        Generate Random Data
      </button>

      {sorted.length > 0 && (
        <>
          <p><strong>Raw Data (sorted):</strong> {sorted.join(", ")}</p>
          <p><strong>Mean:</strong> {mean.toFixed(2)}</p>
          <p><strong>Median:</strong> {median}</p>
          <p><strong>Mode(s):</strong> {modes || "No mode"}</p>
          <p><strong>Range:</strong> {range}</p>

          {/* ---------- VISUAL ---------- */}
          <svg
            width={SVG_W}
            height={SVG_H + 40}
            style={{ background: "#fafafa", border: "1px solid #ddd" }}
          >
            {/* Title */}
            <text
              x={SVG_W / 2}
              y={20}
              fontSize="14"
              textAnchor="middle"
              fontWeight="bold"
            >
              Data Distribution: [{sorted.join(", ")}]
            </text>

            {/* Light horizontal grid */}
            {[...Array(5)].map((_, i) => {
              const y = 40 + (SVG_H - 40) * (i / 5);
              return (
                <line
                  key={i}
                  x1={0}
                  y1={y}
                  x2={SVG_W}
                  y2={y}
                  stroke="#eee"
                />
              );
            })}

            {/* x‑axis */}
            <line
              x1={0}
              y1={SVG_H + 10}
              x2={SVG_W}
              y2={SVG_H + 10}
              stroke="#777"
            />

            {/* Major ticks & labels */}
            {[0, 25, 50, 75, 100].map(v => (
              <g key={v}>
                <line
                  x1={scaleX(v)}
                  y1={SVG_H + 10}
                  x2={scaleX(v)}
                  y2={SVG_H + 4}
                  stroke="#777"
                />
                <text
                  x={scaleX(v)}
                  y={SVG_H + 24}
                  fontSize="10"
                  textAnchor="middle"
                >
                  {v}
                </text>
              </g>
            ))}

            {/* Histogram bars */}
            {bins.map((ct, i) => {
              const h = (ct / maxBin) * (SVG_H - 60);
              return (
                <rect
                  key={i}
                  x={i * BIN_W + 2}
                  y={SVG_H - h - 20}
                  width={BIN_W - 4}
                  height={h || 2}
                  fill="#FFC107"
                  stroke="#FFA000"
                  fillOpacity={0.8}
                />
              );
            })}

            {/* Bell‑curve dashed line */}
            {bellPts.length > 0 && (
              <path
                d={
                  "M" +
                  bellPts
                    .map(([x, y]) => `${x.toFixed(1)},${(y + 20).toFixed(1)}`)
                    .join(" L ")
                }
                fill="none"
                stroke="#9c27b0"
                strokeWidth={2}
                strokeDasharray="4 3"
              />
            )}

            {/* Mean marker */}
            {mean !== null && (
              <>
                <line
                  x1={scaleX(mean)}
                  y1={20}
                  x2={scaleX(mean)}
                  y2={SVG_H - 20}
                  stroke="#F44336"
                  strokeDasharray="6 4"
                  strokeWidth={2}
                />
                <text
                  x={scaleX(mean) + 4}
                  y={18}
                  fontSize="10"
                  fill="#F44336"
                >
                  Mean
                </text>
              </>
            )}

            {/* Median marker */}
            {median !== null && (
              <>
                <line
                  x1={scaleX(median)}
                  y1={20}
                  x2={scaleX(median)}
                  y2={SVG_H - 20}
                  stroke="#4CAF50"
                  strokeDasharray="4 2"
                  strokeWidth={2}
                />
                <text
                  x={scaleX(median) + 4}
                  y={30}
                  fontSize="10"
                  fill="#4CAF50"
                >
                  Median
                </text>
              </>
            )}

            {/* Mode marker */}
            {modes && (
              <>
                <line
                  x1={scaleX(Number(modes.split(",")[0]))}
                  y1={20}
                  x2={scaleX(Number(modes.split(",")[0]))}
                  y2={SVG_H - 20}
                  stroke="#FFA000"
                  strokeDasharray="2 2"
                  strokeWidth={2}
                />
                <text
                  x={scaleX(Number(modes.split(",")[0])) + 4}
                  y={42}
                  fontSize="10"
                  fill="#FFA000"
                >
                  Mode
                </text>
              </>
            )}

            {/* Legend */}
            <rect
              x={SVG_W - 140}
              y={30}
              width={130}
              height={60}
              fill="#fff"
              stroke="#ccc"
            />
            <text x={SVG_W - 130} y={45} fontSize="10" fill="#F44336">
              — Mean
            </text>
            <text x={SVG_W - 130} y={60} fontSize="10" fill="#4CAF50">
              — Median
            </text>
            <text x={SVG_W - 130} y={75} fontSize="10" fill="#FFA000">
              — Mode
            </text>
          </svg>

          <p style={{ fontSize: "0.9rem" }}>
            <em>
              Bars show how many values fall in each {STEP.toFixed(0)}‑point
              interval; the colored dashed lines mark Mean, Median, and Mode.
            </em>
          </p>
        </>
      )}
    </div>
  );
}
